import { IPageParams } from './IApi';

export interface IOrderFilterState {
  createdFrom: string; // 'yyyy-MM-dd', '' means unbounded
  createdTill: string;

  // -- Search toolbar. All partial matches on the server except `id`, which is
  // -- the numeric primary key and is matched exactly. Empty fields are dropped
  // -- from the request, so what remains combines as an AND.
  orderId: string;
  id: string;
  fromSite: string;
  siName: string;
  siEmail: string;
  siCity: string;
  siState: string;
  siZip: string;

  // -- Orders carrying an item from this vendor, by display name. The server
  // -- turns it into supplier keys: vendors and orders are in different
  // -- schemas, so there is nothing to join on. Set by the by-vendor
  // -- drill-down, never by the search toolbar.
  vendorName?: string;

  // -- "year make model", matched against the same expression the by-vehicle
  // -- report groups by. Set by that drill-down only.
  vehicle?: string;

  // -- The item name the by-part-type report groups by. Set by that
  // -- drill-down only.
  partType?: string;

  // -- Matched exactly, not partially - it is the whole value the by-status
  // -- report groups by. Set by that drill-down only.
  status?: string;

  // -- The site the by-website report groups by. Distinct from `fromSite`
  // -- above, which the toolbar matches partially: this one is exact, or
  // -- a.example.com's orders would land in example.com's drill-down.
  website?: string;

  // -- The shipping state the by-state report groups by. Exact, where the
  // -- toolbar's `siState` matches partially. Set by that drill-down only.
  state?: string;

  // -- The market area the by-DMA report groups by. The server resolves it to
  // -- the zips it covers: zip codes and orders are in different databases, so
  // -- there is nothing to join on. Set by that drill-down only.
  dma?: string;

  // -- The stored pi_type code the by-payment-type report groups by ("st"),
  // -- not the label shown for it. Set by that drill-down only.
  paymentType?: string;
}

/** Everything the search toolbar can set, cleared. Dates are left alone. */
export const EMPTY_ORDER_SEARCH = {
  orderId: '',
  id: '',
  fromSite: '',
  siName: '',
  siEmail: '',
  siCity: '',
  siState: '',
  siZip: '',
};

// --
// -- Turns the page params and filter into the query OrderFilter + Pageable
// -- expect.
// --
// -- Kept out of order.api.ts, and free of anything but types, so it can be
// -- exercised on its own: the trimming and dropping rules here are where a
// -- search quietly returns the wrong rows.
// --
export const buildOrderSearchParams = (
  { sort, sortDir, q, ...params }: IPageParams & IOrderFilterState,
): Record<string, string | number> => {
  const query: Record<string, string | number> = {};

  Object.entries(params).forEach(([key, value]) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    // -- An empty field is not a filter. Note 0 must survive this: it is a
    // -- valid page number.
    if (trimmed === '' || trimmed === undefined || trimmed === null) return;

    // -- `id` binds to a Long on the server, so anything non-numeric comes back
    // -- a 400 rather than an empty result. Drop it instead.
    if (key === 'id' && !/^\d+$/.test(String(trimmed))) return;

    query[key] = trimmed as string | number;
  });

  if (sort) query.sort = `${sort},${sortDir}`;

  return query;
};
